/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
import { routerActions } from "react-router-redux";
import { call, put, select } from "redux-saga/effects";

import * as accountsMiddleware from "middleware/accounts";
import { bankedBeneficiary } from "middleware/checks";
import * as debinMiddleware from "middleware/debin";
import { actions as accountsActions } from "reducers/accounts";
import { actions as addLimitRequestActions, selectors as addLimitRequestSelectors } from "reducers/addLimitRequest";
import { actions as cedipActions } from "reducers/cedip";
import { actions as checksActions } from "reducers/checks";
import { actions as debinActions } from "reducers/debin";
import { actions as directDebitActions } from "reducers/directDebit";
import { actions as echeqMassManagementActions } from "reducers/echeqMassManagement";
import { actions as echeqRuleActions } from "reducers/echeqRule";
import { actions as ecomexActions } from "reducers/ecomex";
import { selectors as formSelectors } from "reducers/form";
import { actions as fundcorpActions } from "reducers/fundcorp";
import { actions as multipleTransferActions } from "reducers/multiple-transfers";
import { actions as notificationActions } from "reducers/notification";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import { actions as thirdPaymentActions } from "reducers/thirdPayment";
import { actions as transactionActions, selectors as transactionsSelectors } from "reducers/transactions";
import { actions as transferActions } from "reducers/transfer";
import { history } from "store";
import * as config from "util/config";
import { validateMessageCancellation } from "util/fundcorp";
import * as i18n from "util/i18n";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";
import { buildAddLimitFinishedConfirmationLocation } from "util/addLimitRequestNavigation";
import {
    getCredinTicketPath,
    getRequestCredinPath,
    getTransferExecutePath,
    getTransferTicketPath,
    isTransferHistoricPath,
} from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled, isUxMyAccountsImprovementsEnabled } from "util/v3ShellLayoutSync";

/**
 * Con Mis cuentas v3 + dashboard UX, el ticket de apertura vive en `/v3/account/create/:status/:id`.
 * Sin esas flags se mantiene `/accountOpening/transaction` (ticket legacy).
 * @param {string} idTransactionStatus
 * @param {string} idTransaction
 */
function accountOpeningPostConfirmPath(idTransactionStatus, idTransaction) {
    if (
        isUxMyAccountsImprovementsEnabled() &&
        isUxDashboardImprovementsEnabled() &&
        idTransactionStatus &&
        idTransaction
    ) {
        return `/v3/account/create/${encodeURIComponent(idTransactionStatus)}/${encodeURIComponent(
            String(idTransaction),
        )}`;
    }
    return "/accountOpening/transaction";
}

export function* redirectCancelled(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        signatures,
        idTransactionStatus,
        modificationDateTime,
        submitDateTime,
        pendingDispatch,
    } = transaction;
    transaction.data.submitDateTime = submitDateTime;

    switch (idActivity) {
        case "change.status.debin.recurrency": {
            yield call(actionRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/action/transaction"));
            break;
        }
        case "endorse.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/cancelled/endorse"));
            break;
        }
        case "negotiate.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/cancelled/endorse"));
            break;
        }
        case "load.debin.recurrency": {
            yield call(loadRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/load/transaction"));
            break;
        }
        case "account.checks": {
            yield put(
                checksActions.checksRequestData({
                    ...transaction.data,
                    creatorFullName,
                    signatures,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/checks/transaction"));
            break;
        }
        case "accounts.opening": {
            yield put(
                accountsActions.accountOpeningData({
                    ...transaction.data,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                }),
            );
            yield put(
                routerActions.push(accountOpeningPostConfirmPath(idTransactionStatus, transaction.idTransaction)),
            );
            break;
        }
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push(getTransferTicketPath("CANCELLED")));
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push(getTransferTicketPath("CANCELLED")));
            break;
        }
        case "transfers.toContact.send":
        case "transfersCVU.toContact.send":
        case "transfers.thirdParties.send":
        case "transfersCVU.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push(getTransferTicketPath("CANCELLED")));
            break;
        }
        case "transfers.toContact.tad.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push(getTransferTicketPath("CANCELLED")));
            break;
        }
        case "credinCVU.sentRequest.send":
        case "credin.sentRequest.send": {
            yield put(
                debinActions.loadConfirmCredin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push(getCredinTicketPath()));
            break;
        }
        case "debinCVU.incoming.accept.send":
        case "chargeback.recurrency":
        case "chargeback.pull":
        case "debin.incoming.accept.send": {
            yield put(
                debinActions.loadConfirmIncomingDebin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.replace("/confirmIncomingDebin/transaction"));
            break;
        }
        case "payments.makePayment": {
            yield put(
                servicePaymentsActions.loadConfirmServicePayments({
                    ...transaction.data,
                    summaryInfo: transaction.data.summaryInfo,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.push("/newPayment/cancelled"));
            break;
        }
        case "pay.multiline.send": {
            const isCancelling = yield select(formSelectors.getIsCancellingTransaction);
            const { cancelledFromMenu } = transaction.data;
            const sucessMsjMvp = config.get("frontend.paymentThirdParties.Tooltip.OK");
            const paymentThirdPartiesFunctionalities = config.getBoolean(
                "frontend.show.paymentThirdParties.functionalities",
                false,
            );
            if (isCancelling && cancelledFromMenu) {
                if (paymentThirdPartiesFunctionalities) {
                    yield put(notificationActions.showNotification(sucessMsjMvp, "success", ["forms"]));
                } else {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("thirdpayment.send.cancelled.message"),
                            "success",
                            ["forms"],
                        ),
                    );
                }
                yield put(routerActions.push("/payThirdParties/list"));
            } else {
                yield put(
                    thirdPaymentActions.loadConfirmThirdPayment({
                        ...transaction.data,
                        currency: transaction.data.currency,
                        amount: transaction.data.amount,
                        creatorFullName,
                        userEditorFullName,
                        idTransactionStatus,
                        modificationDateTime,
                        creationDate: creationDateTime,
                        idTransaction: transaction.idTransaction,
                        idActivity,
                        pendingLines: transaction?.data?.pendingLines,
                        processedFileData: transaction?.data?.processedFileData,
                        idUserCreator: transaction?.idUserCreator,
                    }),
                );
                yield put(routerActions.push("/payThirdParties/cancelled"));
            }
            break;
        }
        case "emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/cancelled/emit"));
            break;
        }
        case "external.emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/cancelled/external/emit"));
            break;
        }
        case "manage.echeq.rules": {
            yield call(createEcheqRuleTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqRules/create/transaction"));
            break;
        }
        case "direct.debit.create.send": {
            yield call(directDebitTransactionData, idActivity, transaction);
            yield put(routerActions.push("/directDebit/transaction"));
            break;
        }
        case "fundcorp.cancel.send": {
            yield call(cancelFundTransactionData, transaction);
            yield put(routerActions.push("/cancelSubscribe/transaction"));
            break;
        }
        case "cesion.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/cancelled/cesion"));
            break;
        }
        case "accept.return.echeqs": {
            yield call(echeqAcceptReturnActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/return/accept/transaction"));
            break;
        }
        case "scheduled.transactions.cancel": {
            yield call(loadCanceledSchedule, idActivity, transaction);
            const { type } = transaction.data;
            if (type && type === "subscribe") {
                yield put(routerActions.replace("/subscribe/confirmation"));
            } else if (type && type === "rescue") {
                yield put(routerActions.replace("/fundcorpOperations/rescue/confirmation"));
            } else {
                yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            }
            break;
        }
        case "fundcorp.register.rescue.request": {
            yield call(fundcorpRescueTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/fundcorpOperations/rescue/cancelled"));
            break;
        }
        case "fundcorp.register.suscription.request": {
            yield call(fundcorpSubscribeTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/subscribe/cancelled"));
            break;
        }
        case "external.emit.massive.echeq.activity":
        case "emit.massive.echeqs.send": {
            yield call(echeqMassiveTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/emitMassive/cancelled"));
            break;
        }
        case "transfer.multiple.send": {
            yield put(
                multipleTransferActions.loadMultipleTransfer({
                    ...transaction.data,
                    debitAccount: transaction.data.account,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idUserCreator: transaction?.idUserCreator,
                }),
            );
            yield put(routerActions.replace("/transfer/multiple/cancelled"));
            break;
        }
        case "ecomex.send.accept.transaction": {
            yield put(
                ecomexActions.loadEcomexTransaction({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace("/ecomex/cancelled"));
            break;
        }
        case "send.external.transaction": {
            yield put(
                transactionActions.loadExternalTransaction({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/externaltransactions/cancelled"));
            break;
        }
        case "temporary.limit.increase.request": {
            yield put(
                addLimitRequestActions.loadAddLimitTransaction({
                    ...transaction.data,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    signatures,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(addLimitRequestActions.setFetching(false));
            yield put(routerActions.push("/addlimitrequest/cancelled"));
            break;
        }
        default: {
            break;
        }
    }
}
export function* redirectPending(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const control = transaction.control ?? transaction.data?.control;
    const newRecipient = transaction.newRecipient ?? transaction.data?.newRecipient;
    const { creationDateTime, signatures, idTransactionStatus, modificationDateTime, pendingDispatch } = transaction;

    switch (idActivity) {
        case "change.status.debin.recurrency": {
            yield call(actionRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/action/transaction"));
            break;
        }
        case "endorse.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/endorse"));
            break;
        }
        case "negotiate.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/endorse"));
            break;
        }
        case "echeq.request-certificate.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/certificate"));
            break;
        }
        case "load.debin.recurrency": {
            yield call(loadRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/load/transaction"));
            break;
        }
        case "accounts.opening": {
            yield put(
                accountsActions.accountOpeningData({
                    ...transaction.data,
                    creatorFullName,
                    signatures,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(
                routerActions.push(accountOpeningPostConfirmPath(idTransactionStatus, transaction.idTransaction)),
            );
            break;
        }
        case "account.checks": {
            yield put(
                checksActions.checksRequestData({
                    ...transaction.data,
                    creatorFullName,
                    signatures,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/checks/transaction"));
            break;
        }
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("PENDING")));
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("PENDING")));
            break;
        }
        case "transfers.toContact.send":
        case "transfersCVU.toContact.send":
        case "transfers.thirdParties.send":
        case "transfersCVU.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("PENDING")));
            break;
        }
        case "transfers.toContact.tad.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("PENDING")));
            break;
        }
        case "scheduled.transactions.cancel": {
            yield call(loadCanceledSchedule, idActivity, transaction);
            const { type } = transaction.data;
            if (type && type === "subscribe") {
                yield put(routerActions.replace("/subscribe/pending"));
            } else if (type && type === "rescue") {
                yield put(routerActions.replace("/fundcorpOperations/rescue/pending"));
            } else {
                yield put(routerActions.replace(getTransferTicketPath("PENDING")));
            }
            break;
        }
        case "credinCVU.sentRequest.send":
        case "credin.sentRequest.send": {
            yield put(
                debinActions.loadConfirmCredin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    pendingDispatch,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    control,
                }),
            );
            yield put(routerActions.push(getCredinTicketPath()));
            break;
        }
        case "debinCVU.incoming.accept.send":
        case "chargeback.recurrency":
        case "chargeback.pull":
        case "debin.incoming.accept.send": {
            yield put(
                debinActions.loadConfirmIncomingDebin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.push("/confirmIncomingDebin/transaction"));
            break;
        }
        case "pay.multiline.send": {
            yield put(
                thirdPaymentActions.loadConfirmThirdPayment({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    signatures,
                    pendingLines: transaction?.data?.pendingLines,
                    processedFileData: transaction?.data?.processedFileData,
                    idUserCreator: transaction?.idUserCreator,
                    control,
                }),
            );
            yield put(routerActions.replace("/payThirdParties/pending"));
            break;
        }
        case "payments.makePayment": {
            yield put(
                servicePaymentsActions.loadConfirmServicePayments({
                    ...transaction.data,
                    summaryInfo: transaction.data.summaryInfo,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace("/newPayment/pending"));
            break;
        }
        case "emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/emit"));
            break;
        }
        case "external.emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/external/emit"));
            break;
        }
        case "manage.echeq.rules": {
            yield call(createEcheqRuleTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqRules/create/transaction"));
            break;
        }
        case "direct.debit.create.send": {
            yield call(directDebitTransactionData, idActivity, transaction);
            yield put(routerActions.push("/directDebit/transaction"));
            break;
        }
        case "fundcorp.cancel.send": {
            yield call(cancelFundTransactionData, transaction);
            yield put(routerActions.push("/cancelSubscribe/transaction"));
            break;
        }
        case "cesion.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/pending/cesion"));
            break;
        }
        case "accept.return.echeqs": {
            yield call(echeqAcceptReturnActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/return/accept/transaction"));
            break;
        }
        case "external.emit.massive.echeq.activity":
        case "emit.massive.echeqs.send": {
            yield call(echeqMassiveTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/emitMassive/pending"));
            break;
        }
        case "transfer.multiple.send": {
            yield put(
                multipleTransferActions.loadMultipleTransfer({
                    ...transaction.data,
                    debitAccount: transaction.data.account,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idUserCreator: transaction?.idUserCreator,
                    control,
                }),
            );
            yield put(routerActions.replace("/transfer/multiple/pending"));
            break;
        }
        case "ecomex.send.accept.transaction": {
            yield put(
                ecomexActions.loadEcomexTransaction({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/ecomex/pending"));
            break;
        }
        case "send.external.transaction": {
            yield put(
                transactionActions.loadExternalTransaction({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.push("/externaltransactions/pending"));
            break;
        }
        case "temporary.limit.increase.request": {
            yield put(
                addLimitRequestActions.loadAddLimitTransaction({
                    ...transaction.data,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    signatures,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(addLimitRequestActions.setFetching(false));
            yield put(routerActions.push("/addlimitrequest/pending"));
            break;
        }
        default: {
            break;
        }
    }

    if (control && newRecipient) {
        yield put(
            notificationActions.showNotification(config.get("frontend.NewRecipients.snackbar"), "warning", [
                "pendingTransfer",
                "forms",
                "multiple.transfers",
            ]),
        );
    }
}

export function* redirectPendingToken(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, signatures, idTransactionStatus, modificationDateTime, pendingDispatch } = transaction;

    switch (idActivity) {
        case "ecomex.send.accept.transaction": {
            yield put(
                ecomexActions.loadEcomexTransaction({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/ecomex/transaction"));
            break;
        }
        default: {
            break;
        }
    }
}

export function* redirectDraft(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, idTransactionStatus, modificationDateTime } = transaction;
    const savedTransactionRoute = yield select(transactionsSelectors.getTransactionRoute);
    const draftTransactionListGoBackPathForLists = resolveDraftTransactionListGoBack(
        history.location?.pathname,
        savedTransactionRoute,
    );

    switch (idActivity) {
        case "account.checks": {
            yield put(
                checksActions.checksRequestData({
                    ...transaction.data,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    returnToPending: true,
                    checksTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            yield put(routerActions.push("/checks/modify"));
            break;
        }

        case "accounts.opening": {
            yield put(
                accountsActions.accountOpeningData({
                    ...transaction.data,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            const accountOpeningModifyPath =
                isUxMyAccountsImprovementsEnabled() && isUxDashboardImprovementsEnabled()
                    ? "/v3/accounts/edit"
                    : "/accountOpening/modify";
            yield put(routerActions.push(accountOpeningModifyPath));
            break;
        }
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            {
                const { location } = history;
                if (isTransferHistoricPath(location.pathname)) {
                    yield put(routerActions.push(getTransferExecutePath("internal", { suffix: "modify" })));
                } else {
                    yield put(routerActions.replace(getTransferExecutePath("internal", { suffix: "modify" })));
                }
            }
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            {
                const { location } = history;
                if (isTransferHistoricPath(location.pathname)) {
                    yield put(routerActions.push(getTransferExecutePath("internal", { suffix: "modify" })));
                } else {
                    yield put(routerActions.replace(getTransferExecutePath("internal", { suffix: "modify" })));
                }
            }
            break;
        }
        case "transfers.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            if (transaction.data.toContact) {
                yield put(routerActions.replace(getTransferExecutePath("toContact", { suffix: "modify" })));
            } else {
                const { location } = history;
                if (isTransferHistoricPath(location.pathname)) {
                    yield put(routerActions.push(getTransferExecutePath("thirdParties", { suffix: "modify" })));
                } else {
                    yield put(routerActions.replace(getTransferExecutePath("thirdParties", { suffix: "modify" })));
                }
            }
            break;
        }
        case "transfersCVU.thirdParties.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            {
                const { location } = history;
                if (isTransferHistoricPath(location.pathname)) {
                    yield put(routerActions.push(getTransferExecutePath("thirdParties", { suffix: "modify" })));
                } else {
                    yield put(routerActions.replace(getTransferExecutePath("thirdParties", { suffix: "modify" })));
                }
            }
            break;
        }
        case "transfersCVU.toContact.send":
        case "transfers.toContact.tad.send":
        case "transfers.toContact.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    returnToPending: true,
                    draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                }),
            );
            {
                const { location } = history;
                if (isTransferHistoricPath(location.pathname)) {
                    yield put(routerActions.push(getTransferExecutePath("toContact", { suffix: "modify" })));
                } else {
                    yield put(routerActions.replace(getTransferExecutePath("toContact", { suffix: "modify" })));
                }
            }
            break;
        }
        case "credinCVU.sentRequest.send":
        case "credin.sentRequest.send": {
            const cbuList = transaction?.data?.cbuList || [];
            yield put(
                debinActions.setDebinSeller(
                    {
                        ...transaction.data,
                        idTransaction: transaction.idTransaction,
                        returnToPending: true,
                        draftTransactionListGoBackPath: draftTransactionListGoBackPathForLists,
                    },
                    cbuList,
                ),
            );
            yield put(debinActions.setCredinDraftActivity(transaction.idTransaction, idActivity));
            yield put(debinActions.setIsBackFromSummary(getRequestCredinPath()));
            break;
        }

        default: {
            break;
        }
    }
}
export function* redirectFinished(idActivity, transaction, newTransaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        signatures,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        submitDateTime,
        control,
    } = transaction;
    transaction.data.submitDateTime = submitDateTime;

    switch (idActivity) {
        case "change.status.debin.recurrency": {
            yield call(actionRecurrencyDebin, idActivity, transaction, newTransaction);
            yield put(routerActions.push("/payments/recurrent/action/transaction"));
            break;
        }
        case "endorse.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/confirm/endorse"));
            break;
        }
        case "negotiate.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/confirm/endorse"));
            break;
        }
        case "load.debin.recurrency": {
            yield call(loadRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/load/transaction"));
            break;
        }
        case "accounts.opening": {
            yield put(
                accountsActions.accountOpeningData({
                    ...transaction.data,
                    signatures,
                    creatorFullName,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    modificationDateTime,
                    userEditorFullName,
                    idTransactionStatus,
                }),
            );
            yield put(
                routerActions.push(accountOpeningPostConfirmPath(idTransactionStatus, transaction.idTransaction)),
            );
            break;
        }
        case "account.checks": {
            yield put(
                checksActions.checksRequestData({
                    ...transaction.data,
                    creatorFullName,
                    signatures,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/checks/transaction"));
            break;
        }
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    ...(newTransaction ? { newTransaction: true } : {}),
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    ...(newTransaction ? { newTransaction: true } : {}),
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            break;
        }
        case "transfers.toContact.send":
        case "transfersCVU.toContact.send":
        case "transfers.thirdParties.send":
        case "transfersCVU.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                    newTransaction,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            break;
        }
        case "transfers.toContact.tad.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            break;
        }
        case "scheduled.transactions.cancel": {
            yield call(loadCanceledSchedule, idActivity, transaction);
            const { type } = transaction.data;
            if (type && type === "subscribe") {
                yield put(routerActions.replace("/subscribe/confirmation"));
            } else if (type && type === "rescue") {
                yield put(routerActions.replace("/fundcorpOperations/rescue/confirmation"));
            } else {
                yield put(routerActions.replace(getTransferTicketPath("FINISHED")));
            }
            break;
        }
        case "credinCVU.sentRequest.send":
        case "credin.sentRequest.send": {
            yield put(
                debinActions.loadConfirmCredin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    control,
                }),
            );
            yield put(routerActions.push(getCredinTicketPath()));
            break;
        }
        case "debinCVU.incoming.accept.send":
        case "chargeback.recurrency":
        case "chargeback.pull":
        case "debin.incoming.accept.send": {
            yield put(
                debinActions.loadConfirmIncomingDebin({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    control,
                }),
            );
            yield put(routerActions.push("/confirmIncomingDebin/transaction"));
            break;
        }
        case "pay.multiline.send": {
            yield put(
                thirdPaymentActions.loadConfirmThirdPayment({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingLines: transaction?.data?.pendingLines,
                    processedFileData: transaction?.data?.processedFileData,
                    idUserCreator: transaction?.idUserCreator,
                    control,
                }),
            );
            if (newTransaction) {
                yield put(
                    notificationActions.showNotification(i18n.get("thirdpayment.send.success.message"), "success", [
                        "forms",
                    ]),
                );
            }
            yield put(routerActions.push("/payThirdParties/confirmation"));
            break;
        }
        case "echeq.admit.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.anulate-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.anulate-endorse.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.anulate-echeq.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.repudiate.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.deposit.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.custody.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.request-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }

        case "echeq.rescue.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.repudiate-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.admit-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "payments.makePayment": {
            yield put(
                servicePaymentsActions.loadConfirmServicePayments({
                    ...transaction.data,
                    summaryInfo: transaction.data.summaryInfo,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                }),
            );
            yield put(routerActions.replace("/newPayment/confirmation"));
            break;
        }
        case "emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/confirm/emit"));
            break;
        }
        case "external.emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/confirm/external/emit"));
            break;
        }
        case "manage.echeq.rules": {
            yield call(createEcheqRuleTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqRules/create/transaction"));
            break;
        }
        case "direct.debit.create.send": {
            yield call(directDebitTransactionData, idActivity, transaction);
            yield put(routerActions.push("/directDebit/transaction"));
            break;
        }
        case "mass.echeq.manage.admit.send":
        case "mass.echeq.manage.pay.send": {
            yield call(massEcheqManageTransactionData, idActivity, transaction);
            yield put(routerActions.replace("/v2/echeqs/mass-management/ticket"));
            break;
        }
        case "fundcorp.cancel.send": {
            yield call(cancelFundTransactionData, transaction);
            yield put(routerActions.push("/cancelSubscribe/transaction"));
            break;
        }
        case "cesion.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/confirm/cesion"));
            break;
        }
        case "echeq.anulate-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.repudiate-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "accept.return.echeqs": {
            yield call(echeqAcceptReturnActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/return/accept/transaction"));
            break;
        }
        case "echeq.request-certificate.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }

        case "fundcorp.register.rescue.request": {
            yield call(fundcorpRescueTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/fundcorpOperations/rescue/confirmation"));
            break;
        }

        case "fundcorp.register.suscription.request": {
            yield call(fundcorpSubscribeTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/subscribe/confirmation"));
            break;
        }
        case "external.emit.massive.echeq.activity":
        case "emit.massive.echeqs.send": {
            yield call(echeqMassiveTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/emitMassive/confirmation"));
            break;
        }
        case "transfer.multiple.send": {
            yield put(
                multipleTransferActions.loadMultipleTransfer({
                    ...transaction.data,
                    debitAccount: transaction.data.account,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idUserCreator: transaction?.idUserCreator,
                    control,
                }),
            );
            yield put(routerActions.replace("/transfer/multiple/confirmation"));
            break;
        }
        case "cedip.reject": {
            yield put(
                cedipActions.loadTransactionData({
                    ...transaction.data,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace("/cedip/ticket"));
            break;
        }
        case "ecomex.send.accept.transaction": {
            yield put(
                ecomexActions.loadEcomexTransaction({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace("/ecomex/finished"));
            break;
        }
        case "send.external.transaction": {
            yield put(
                transactionActions.loadExternalTransaction({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.push("/externaltransactions/confirmation"));
            break;
        }
        case "temporary.limit.increase.request": {
            yield put(
                addLimitRequestActions.loadAddLimitTransaction({
                    ...transaction.data,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    signatures,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(addLimitRequestActions.setFetching(false));
            const addLimitPrevPath = yield select(addLimitRequestSelectors.getPrevPath);
            yield put(routerActions.push(buildAddLimitFinishedConfirmationLocation(newTransaction, addLimitPrevPath)));

            break;
        }
        default: {
            break;
        }
    }
}

export function* redirectFailed(idActivity, transaction, newTransaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        signatures,
        pendingDispatch,
        idTransactionStatus,
        submitDateTime,
        modificationDateTime,
        control,
    } = transaction;
    transaction.data.submitDateTime = submitDateTime;
    switch (idActivity) {
        case "change.status.debin.recurrency": {
            yield call(actionRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/action/transaction"));
            break;
        }
        case "echeq.admit.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "accounts.opening": {
            yield put(
                accountsActions.accountOpeningData({
                    ...transaction.data,
                    creatorFullName,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    returnToPending: true,
                }),
            );
            yield put(
                routerActions.push(accountOpeningPostConfirmPath(idTransactionStatus, transaction.idTransaction)),
            );
            break;
        }
        case "echeq.anulate-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "load.debin.recurrency": {
            yield call(loadRecurrencyDebin, idActivity, transaction);
            yield put(routerActions.push("/payments/recurrent/load/transaction"));
            break;
        }
        case "echeq.anulate-echeq.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.anulate-endorse.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.repudiate.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.deposit.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.custody.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.rescue.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/failed/emit"));
            break;
        }
        case "external.emit.echeq.activity": {
            yield call(emitEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/failed/external/emit"));
            break;
        }
        case "manage.echeq.rules": {
            yield call(createEcheqRuleTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqRules/create/transaction"));
            break;
        }
        case "direct.debit.create.send": {
            yield call(directDebitTransactionData, idActivity, transaction);
            yield put(routerActions.push("/directDebit/transaction"));
            break;
        }
        case "mass.echeq.manage.admit.send":
        case "mass.echeq.manage.pay.send": {
            yield call(massEcheqManageTransactionData, idActivity, transaction);
            yield put(routerActions.replace("/v2/echeqs/mass-management/ticket"));
            break;
        }
        case "fundcorp.cancel.send": {
            yield call(cancelFundTransactionData, transaction);
            yield put(routerActions.push("/cancelSubscribe/transaction"));
            break;
        }
        case "endorse.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/failed/endorse"));
            break;
        }
        case "negotiate.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/failed/endorse"));
            break;
        }
        case "echeq.repudiate-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.admit-cesion.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "cesion.echeq.activity": {
            yield call(createEcheqTransactionData, idActivity, transaction);
            yield put(routerActions.push("/echeqs/failed/cesion"));
            break;
        }
        case "echeq.request-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.anulate-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "echeq.repudiate-return.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "accept.return.echeqs": {
            yield call(echeqAcceptReturnActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/return/accept/transaction"));
            break;
        }
        case "echeq.request-certificate.action": {
            yield call(echeqTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/transaction/action"));
            break;
        }
        case "payments.makePayment": {
            yield call(servicePaymentsTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/newPayment/confirmation"));
            break;
        }
        case "credinCVU.sentRequest.send":
        case "credin.sentRequest.send": {
            yield put(
                debinActions.loadConfirmCredin({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    pendingDispatch,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    control,
                }),
            );
            yield put(routerActions.push(getCredinTicketPath()));
            break;
        }
        case "debinCVU.incoming.accept.send":
        case "chargeback.recurrency":
        case "chargeback.pull":
        case "debin.incoming.accept.send": {
            yield put(
                debinActions.loadConfirmIncomingDebin({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    control,
                }),
            );
            yield put(routerActions.push("/confirmIncomingDebin/transaction"));
            break;
        }
        case "account.checks": {
            yield put(
                checksActions.checksRequestData({
                    ...transaction.data,
                    creatorFullName,
                    signatures,
                    idTransaction: transaction.idTransaction,
                    creationDate: creationDateTime,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.push("/checks/transaction"));
            break;
        }
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    ...(newTransaction ? { newTransaction: true } : {}),
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FAILED")));
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    ...(newTransaction ? { newTransaction: true } : {}),
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FAILED")));
            break;
        }
        case "transfers.toContact.send":
        case "transfersCVU.toContact.send":
        case "transfers.thirdParties.send":
        case "transfersCVU.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FAILED")));
            break;
        }
        case "transfers.toContact.tad.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("FAILED")));
            break;
        }
        case "pay.multiline.send": {
            const isCancelling = yield select(formSelectors.getIsCancellingTransaction);
            const sucessMsjMvp = config.get("frontend.paymentThirdParties.Tooltip.OK");
            const paymentThirdPartiesFunctionalities = config.getBoolean(
                "frontend.show.paymentThirdParties.functionalities",
                false,
            );
            if (isCancelling) {
                if (paymentThirdPartiesFunctionalities) {
                    yield put(notificationActions.showNotification(sucessMsjMvp, "success", ["form"]));
                } else {
                    yield put(
                        notificationActions.showNotification(
                            i18n.get("thirdpayment.send.cancelled.message"),
                            "success",
                            ["form"],
                        ),
                    );
                }

                yield put(routerActions.push("/payThirdParties/list"));
            } else {
                yield put(
                    thirdPaymentActions.loadConfirmThirdPayment({
                        ...transaction.data,
                        currency: transaction.data.currency,
                        amount: transaction.data.amount,
                        creatorFullName,
                        userEditorFullName,
                        idTransactionStatus,
                        modificationDateTime,
                        creationDate: creationDateTime,
                        idTransaction: transaction.idTransaction,
                        reference: transaction.data.reference,
                        idActivity,
                        pendingLines: transaction?.data?.pendingLines,
                        processedFileData: transaction?.data?.processedFileData,
                        idUserCreator: transaction?.idUserCreator,
                    }),
                );
                if (newTransaction) {
                    yield put(
                        notificationActions.showNotification(i18n.get("thirdpayment.send.success.message"), "success", [
                            "forms",
                        ]),
                    );
                }
                yield put(routerActions.push("/payThirdParties/confirmation"));
            }
            break;
        }
        case "fundcorp.register.rescue.request": {
            yield call(fundcorpRescueTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/fundcorpOperations/rescue/failed"));
            break;
        }

        case "fundcorp.register.suscription.request": {
            yield call(fundcorpSubscribeTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/subscribe/failed"));
            break;
        }
        case "external.emit.massive.echeq.activity":
        case "emit.massive.echeqs.send": {
            yield call(echeqMassiveTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/echeqs/emitMassive/failed"));
            break;
        }
        case "transfer.multiple.send": {
            yield put(
                multipleTransferActions.loadMultipleTransfer({
                    ...transaction.data,
                    debitAccount: transaction.data.account,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    idUserCreator: transaction?.idUserCreator,
                    control,
                }),
            );
            yield put(routerActions.replace("/transfer/multiple/failed"));
            break;
        }
        case "ecomex.send.accept.transaction": {
            yield put(
                ecomexActions.loadEcomexTransaction({
                    ...transaction.data,
                    currency: transaction.data.currency,
                    amount: transaction.data.amount,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace("/ecomex/failed"));
            break;
        }
        case "send.external.transaction": {
            yield put(
                transactionActions.loadExternalTransaction({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.push("/externaltransactions/failed"));
            break;
        }
        case "temporary.limit.increase.request": {
            yield put(
                addLimitRequestActions.loadAddLimitTransaction({
                    ...transaction.data,
                    creatorFullName,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    signatures,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(addLimitRequestActions.setFetching(false));
            yield put(routerActions.push("/addlimitrequest/failed"));
            break;
        }
        default: {
            break;
        }
    }
}

export function* redirectScheduler(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        signatures,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        control,
    } = transaction;

    switch (idActivity) {
        case "transfers.internal.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("SCHEDULED")));
            break;
        }
        case "transfers.internal.tad.send": {
            yield put(
                transferActions.loadTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("SCHEDULED")));
            break;
        }
        case "transfers.toContact.send":
        case "transfersCVU.toContact.send":
        case "transfers.thirdParties.send":
        case "transfersCVU.thirdParties.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("SCHEDULED")));
            break;
        }
        case "transfers.toContact.tad.send":
        case "transfers.thirdParties.tad.send": {
            yield put(
                transferActions.loadExternalTransfer({
                    ...transaction.data,
                    currency: transaction.data.amount.currency,
                    amount: transaction.data.amount.quantity,
                    creatorFullName,
                    signatures,
                    creationDate: creationDateTime,
                    idTransaction: transaction.idTransaction,
                    idActivity,
                    pendingDispatch,
                    userEditorFullName,
                    idTransactionStatus,
                    modificationDateTime,
                    control,
                }),
            );
            yield put(routerActions.replace(getTransferTicketPath("SCHEDULED")));
            break;
        }
        case "fundcorp.register.rescue.request": {
            yield call(fundcorpRescueTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/fundcorpOperations/rescue/scheduled"));
            break;
        }
        case "fundcorp.register.suscription.request": {
            yield call(fundcorpRescueTransactionActions, idActivity, transaction);
            yield put(routerActions.push("/subscribe/scheduled"));
            break;
        }
        default: {
            break;
        }
    }
}

function* echeqAcceptReturnActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const {
        creationDateTime,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        submitDateTimeAsString,
    } = transaction;
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);

    const response = yield call(bankedBeneficiary, {
        document: transaction.data.echeqList[0].cuit_solic_devol,
        type: "CUIT",
    });

    yield put(
        checksActions.transactionEcheq({
            ...transaction.data,
            userEditorFullName,
            creatorFullName,
            echeq: transaction.data.echeqList[0],
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            pendingDispatch,
            modificationDateTime,
            idTransactionStatus,
            solicitantName: response.data.data.beneficiary.beneficiario_razon_social,
            submitDateTimeAsString,
        }),
    );
}
function* echeqTransactionActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        submitDateTimeAsString,
        signatures,
    } = transaction;
    let echeqData = {};
    if (transaction.data?.action === "REQUEST-CERTIFICATE" && idTransactionStatus === "PENDING") {
        echeqData = transaction.data?.echeqList[0];
    }

    yield put(
        checksActions.transactionEcheq({
            ...transaction.data,
            ...echeqData,
            userEditorFullName,
            creatorFullName,
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            pendingDispatch,
            modificationDateTime,
            idTransactionStatus,
            submitDateTimeAsString,
            signatures,
        }),
    );
}

function* servicePaymentsTransactionActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, idTransactionStatus, modificationDateTime } = transaction;

    yield put(
        servicePaymentsActions.loadConfirmServicePayments({
            ...transaction.data,
            summaryInfo: transaction.data.summaryInfo,
            creatorFullName,
            userEditorFullName,
            idTransactionStatus,
            modificationDateTime,
            creationDate: creationDateTime,
            idTransaction: transaction.idTransaction,
            idActivity,
        }),
    );
}

function* fundcorpRescueTransactionActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, idTransactionStatus, modificationDateTime } = transaction;

    yield put(
        fundcorpActions.loadConfirmRescue({
            ...transaction.data,
            summary: transaction.data.summary,
            creatorFullName,
            userEditorFullName,
            idTransactionStatus,
            modificationDateTime,
            creationDate: creationDateTime,
            idTransaction: transaction.idTransaction,
            idActivity,
        }),
    );
}

function* fundcorpSubscribeTransactionActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, idTransactionStatus, modificationDateTime } = transaction;

    const messageCancellation =
        idTransactionStatus &&
        idTransactionStatus === "FINISHED" &&
        validateMessageCancellation(transaction.data?.numFund, transaction.data?.fundName);

    yield put(
        fundcorpActions.loadConfirmSubscribe({
            ...transaction.data,
            summary: transaction.data.summary,
            creatorFullName,
            userEditorFullName,
            idTransactionStatus,
            modificationDateTime,
            creationDate: creationDateTime,
            idTransaction: transaction.idTransaction,
            idActivity,
            messageCancellation,
        }),
    );
}

function* createEcheqTransactionData(idActivity, transaction) {
    const {
        creationDateTime,
        signatures,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        submitDateTimeAsString,
    } = transaction;
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { beneficiary } = transaction.data;

    const beneficiaryString = beneficiary?.split(" ");

    let document = {};

    if (beneficiaryString && beneficiaryString.length > 1) {
        document = {
            document: beneficiary.split(" ")[1],
            type: beneficiary.split(" ")[0],
        };
    } else {
        document = {
            document: beneficiary.split(" ")[0],
            type: "CUIT",
        };
    }

    const response = yield call(bankedBeneficiary, document);

    const beneficiaryData = response.data.data.beneficiary || {
        beneficiario_documento_tipo: document.type,
        beneficiario_documento: document.document,
        beneficiario_razon_social: "No Encontrado",
    };

    const beneficiaryInfo = {
        document: beneficiaryData.beneficiario_documento_tipo
            ? beneficiaryData.beneficiario_documento_tipo.concat(" ", beneficiaryData.beneficiario_documento)
            : "",
        reference: beneficiaryData.beneficiario_razon_social || "",
    };
    yield put(
        checksActions.transactionEcheq({
            ...transaction.data,
            userEditorFullName,
            beneficiary: beneficiaryInfo,
            echeq: transaction.data.echeqList[0],
            signatures,
            creatorFullName,
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            pendingDispatch,
            modificationDateTime,
            idTransactionStatus,
            submitDateTimeAsString,
        }),
    );
}

function* emitEcheqTransactionData(idActivity, transaction) {
    const {
        creationDateTime,
        signatures,
        pendingDispatch,
        idTransactionStatus,
        modificationDateTime,
        submitDateTimeAsString,
    } = transaction;
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { beneficiary } = transaction.data;

    const document = {
        document: beneficiary.split(" ")[1],
        type: beneficiary.split(" ")[0],
    };
    const response = yield call(bankedBeneficiary, document);

    const accountResponse = yield call(accountsMiddleware.getAccountInfo, transaction.data.emision);

    const { account } = accountResponse.data.data;

    const accountFull = account.number.concat(
        " ",
        account.productType,
        " ",
        i18n.get(`currency.label.${account.currency}`),
    );
    const beneficiaryData = response.data.data.beneficiary || {
        beneficiario_documento_tipo: document.type,
        beneficiario_documento: document.document,
        beneficiario_razon_social: "No Encontrado",
    };

    const beneficiaryInfo = {
        document: beneficiaryData.beneficiario_documento_tipo
            ? beneficiaryData.beneficiario_documento_tipo.concat(" ", beneficiaryData.beneficiario_documento)
            : "",
        reference: beneficiaryData.beneficiario_razon_social || "",
    };
    yield put(
        checksActions.transactionEcheq({
            userEditorFullName,
            ...transaction.data,
            beneficiary: beneficiaryInfo,
            signatures,
            accountFull,
            creatorFullName,
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            pendingDispatch,
            modificationDateTime,
            idTransactionStatus,
            submitDateTimeAsString,
        }),
    );
}

function* createEcheqRuleTransactionData(idActivity, transaction) {
    yield put(echeqRuleActions.echeqRuleTransactionSetData(transaction));
}

function* directDebitTransactionData(idActivity, transaction) {
    yield put(directDebitActions.directDebitTransactionSetData(transaction));
}

function* massEcheqManageTransactionData(idActivity, transaction) {
    const transactionData = { ...transaction, idActivity };
    if (transactionData.data) {
        const { details, ...dataWithoutDetails } = transactionData.data;
        transactionData.data = dataWithoutDetails;
    }
    yield put(echeqMassManagementActions.setTransactionData(transactionData));
}

function* cancelFundTransactionData(transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    yield put(fundcorpActions.cancelSubscribeSetData({ ...transaction, ...transaction?.data, creatorFullName }));
}

function* loadRecurrencyDebin(idActivity, transaction) {
    const { creationDateTime, signatures, idTransactionStatus, modificationDateTime } = transaction;
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);

    const accountResponse = yield call(accountsMiddleware.getAccountInfo, transaction.data.cbu);
    const sellerResponse = yield call(debinMiddleware.getSeller, transaction.data.seller);
    const { account } = accountResponse.data.data;

    const accountFull = account.number.concat(
        " ",
        account.productType,
        " ",
        i18n.get(`currency.label.${account.currency}`),
    );

    yield put(
        debinActions.setdebinPrestacionData({
            userEditorFullName,
            ...transaction.data,
            signatures,
            sellerInfo: sellerResponse?.data?.data?.seller && JSON.parse(sellerResponse?.data?.data?.seller),
            accountFull,
            creatorFullName,
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            modificationDateTime,
            idTransactionStatus,
        }),
    );
}

function* actionRecurrencyDebin(idActivity, transaction, newTransaction) {
    const { creationDateTime, signatures, idTransactionStatus, modificationDateTime } = transaction;
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);

    const accountResponse = yield call(accountsMiddleware.getAccountInfo, transaction.data.cbu);
    const sellerResponse = yield call(debinMiddleware.getSeller, transaction.data.seller);
    const { account } = accountResponse.data.data;

    const accountFull = account?.number?.concat(
        " ",
        account?.productType,
        " ",
        i18n.get(`currency.label.${account?.currency}`),
    );

    yield put(
        debinActions.setRecurrencyDebinAction({
            userEditorFullName,
            ...transaction.data,
            signatures,
            sellerInfo: sellerResponse?.data?.data?.seller && JSON.parse(sellerResponse?.data?.data?.seller),
            accountFull,
            creatorFullName,
            idTransaction: transaction.idTransaction,
            creationDate: creationDateTime,
            idActivity,
            modificationDateTime,
            idTransactionStatus,
            isNewTransaction: newTransaction,
        }),
    );
}

function* loadCanceledSchedule(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const { creationDateTime, signatures, pendingDispatch, idTransactionStatus, modificationDateTime } = transaction;
    const { type } = transaction.data;
    if (type && type === "subscribe") {
        yield call(fundcorpSubscribeTransactionActions, idActivity, transaction);
    } else if (type && type === "rescue") {
        yield call(fundcorpRescueTransactionActions, idActivity, transaction);
    } else {
        yield put(
            transferActions.loadScheduledTransactionCancel({
                ...transaction.data,
                creatorFullName,
                signatures,
                creationDate: creationDateTime,
                idTransaction: transaction.idTransaction,
                idActivity,
                pendingDispatch,
                userEditorFullName,
                idTransactionStatus,
                modificationDateTime,
            }),
        );
    }
}

export function* echeqMassiveTransactionActions(idActivity, transaction) {
    const creatorFullName = transaction.userCreatorLastName.concat(" ", transaction.userCreatorFirstName);
    const userEditorFullName = transaction.userEditorLastName.concat(" ", transaction.userEditorFirstName);
    const {
        creationDateTime,
        signatures,
        idTransactionStatus,
        modificationDateTime,
        pendingDispatch,
        submitDateTimeAsString,
    } = transaction;

    yield put(
        checksActions.transactionEcheq({
            ...transaction.data,
            summary: transaction.data.summary,
            creatorFullName,
            userEditorFullName,
            idTransactionStatus,
            modificationDateTime,
            signatures,
            pendingDispatch,
            creationDate: creationDateTime,
            idTransaction: transaction.idTransaction,
            idActivity,
            submitDateTimeAsString,
        }),
    );
}
